import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError, fmtINR, fmtDate } from "../lib/apiClient";
import { DOC_TYPES, DOC_TYPE_BY_PATH, DOC_STATUS_OPTIONS, statusClass } from "../lib/constants";
import PageHeader from "../components/PageHeader";
import { OrderItemsCell, DeliveryDateCell } from "../components/OrderItemsCell";
import { Plus, MagnifyingGlass, Eye, PencilSimple, Trash, PaperPlaneTilt, Check, Checks, Warning, WhatsappLogo } from "@phosphor-icons/react";
import { toast } from "sonner";

// Maps Gallabox delivery status → tiny icon + tooltip + color
function DeliveryTick({ status, sentAt, phone }) {
  if (!status) return <span className="text-ink-muted/40 text-[10px]">—</span>;
  const t = sentAt ? `${status} · ${fmtDate(sentAt)}${phone ? ` · ${phone}` : ""}` : status;
  if (status === "read") return <span title={t} className="inline-flex items-center text-[#34B7F1]" data-testid="tick-read"><Checks size={14} weight="bold" /></span>;
  if (status === "delivered") return <span title={t} className="inline-flex items-center text-ink-muted" data-testid="tick-delivered"><Checks size={14} weight="bold" /></span>;
  if (status === "sent") return <span title={t} className="inline-flex items-center text-ink-muted" data-testid="tick-sent"><Check size={14} weight="bold" /></span>;
  if (status === "failed") return <span title={t} className="inline-flex items-center text-destructive" data-testid="tick-failed"><Warning size={14} weight="bold" /></span>;
  return <span title={t} className="inline-flex items-center text-ink-muted">{status}</span>;
}

export default function DocumentsListPage() {
  const { docPath } = useParams();
  return <DocumentsList key={docPath} docPath={docPath} />;
}

function DocumentsList({ docPath }) {
  const docType = DOC_TYPE_BY_PATH[docPath];
  const cfg = DOC_TYPES[docType];
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState(cfg?.defaultStatusFilter || "");
  const [sortBy, setSortBy] = useState(cfg?.sortByDelivery ? "delivery" : "newest");
  const [selected, setSelected] = useState(new Set());
  const [deliveries, setDeliveries] = useState({}); // {doc_id: {status, sent_at, ...}}

  const load = async () => {
    const { data } = await api.get("/documents", { params: { doc_type: docType, q: q || undefined, status: statusFilter || undefined } });
    setRows(data);
    setSelected(new Set());
    // Pull WhatsApp delivery ticks for this page (only for sales docs that get sent)
    if (data.length > 0 && ["invoice", "repair_invoice", "quotation", "delivery_challan", "credit_note"].includes(docType)) {
      try {
        const { data: dlv } = await api.post("/whatsapp/deliveries/lookup", { doc_ids: data.map(r => r.id) });
        setDeliveries(dlv || {});
      } catch (err) { console.error('Deliveries load failed:', err); setDeliveries({}); }
    } else {
      setDeliveries({});
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [docType, statusFilter]);

  const remove = async (id) => {
    if (!window.confirm(`Delete this ${cfg.label}?`)) return;
    try { await api.delete(`/documents/${id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const toggle = (id) => {
    setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map(r => r.id)));
  };

  const bulkStatus = DOC_STATUS_OPTIONS[docType] ? "completed" : "sent";
  const bulkMarkSent = async () => {
    if (selected.size === 0) return;
    try {
      await api.patch("/documents/bulk-status", { ids: [...selected], status: bulkStatus });
      toast.success(`Marked ${selected.size} as ${bulkStatus.charAt(0).toUpperCase() + bulkStatus.slice(1)}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  if (!docType) return <div className="p-8">Unknown document type</div>;

  const showWaTicks = ["invoice", "repair_invoice", "quotation", "delivery_challan", "credit_note"].includes(docType);
  const showItems = !!cfg.listItems;
  const showDelivery = !!cfg.showDelivery;
  const showTaxable = cfg.showTax && !cfg.listItems;
  const colSpan = (showTaxable ? 8 : 7) + (showWaTicks ? 1 : 0) + (showItems ? 1 : 0) + (showDelivery ? 1 : 0);
  const statusOpts = DOC_STATUS_OPTIONS[docType] || ["draft", "sent", "accepted", "paid", "cancelled", "rejected", "dispatched"];
  const displayStatus = (s) => (DOC_STATUS_OPTIONS[docType] && ["draft", "sent", "accepted"].includes(s) ? "pending" : s);

  const sorted = [...rows].sort((a, b) => {
    if (sortBy === "delivery") {
      const da = a.expected_delivery_date || "9999-12-31", dbb = b.expected_delivery_date || "9999-12-31";
      if (da !== dbb) return da < dbb ? -1 : 1;
      return (a.doc_date || "") < (b.doc_date || "") ? -1 : 1;
    }
    if (sortBy === "oldest") return (a.doc_date || "") < (b.doc_date || "") ? -1 : 1;
    return (a.created_at || "") > (b.created_at || "") ? -1 : 1;
  });

  return (
    <div data-testid={`documents-list-${docType}`}>
      <PageHeader
        title={cfg.label + "s"}
        subtitle={`All ${cfg.label.toLowerCase()} records`}
        actions={
          <>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted" />
              <input
                data-testid="doc-search"
                value={q} onChange={e => setQ(e.target.value)}
                onBlur={load} onKeyDown={e => e.key === "Enter" && load()}
                placeholder="Search no. or party…"
                className="pl-8 pr-3 py-2 text-sm bg-white border border-input rounded-md outline-none w-60 focus:ring-2 focus:ring-brand-secondary"
              />
            </div>
            <select data-testid="doc-status-filter" className="py-2 px-3 text-sm bg-white border border-input rounded-md outline-none focus:ring-2 focus:ring-brand-secondary" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">All statuses</option>
              {statusOpts.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
            </select>
            {showDelivery && (
              <select data-testid="doc-sort" className="py-2 px-3 text-sm bg-white border border-input rounded-md outline-none focus:ring-2 focus:ring-brand-secondary" value={sortBy} onChange={e => setSortBy(e.target.value)}>
                <option value="delivery">Sort: Delivery date</option>
                <option value="newest">Sort: Newest first</option>
                <option value="oldest">Sort: Oldest first</option>
              </select>
            )}
            <button onClick={() => nav(`/${cfg.path}/new`)} className="btn-primary flex items-center gap-1.5" data-testid="new-doc-button"><Plus size={14} weight="bold" /> New {cfg.label}</button>
          </>
        }
      />

      <div className="p-8">
        {selected.size > 0 && (
          <div className="mb-3 flex items-center justify-between rounded-md border border-border bg-brand-secondary/40 px-4 py-2.5" data-testid="bulk-bar">
            <div className="text-sm font-semibold">{selected.size} selected</div>
            <div className="flex items-center gap-2">
              <button onClick={bulkMarkSent} className="btn-primary inline-flex items-center gap-1.5" data-testid="bulk-mark-sent">
                {bulkStatus === "completed" ? <><Check size={14} weight="bold" /> Mark Completed</> : <><PaperPlaneTilt size={14} weight="bold" /> Mark as Sent</>}
              </button>
              <button onClick={() => setSelected(new Set())} className="btn-secondary text-xs">Clear</button>
            </div>
          </div>
        )}

        <div className="table-wrapper">
          <table className="table-base">
            <thead><tr>
              <th className="w-8"><input type="checkbox" checked={rows.length > 0 && selected.size === rows.length} onChange={toggleAll} data-testid="select-all" /></th>
              <th>Doc No</th><th>Date</th><th>Party</th>{showItems && <th>Items</th>}{showDelivery && <th>Delivery</th>}{showTaxable && <th className="text-right">Taxable</th>}<th className="text-right">Total</th><th>Status</th>{showWaTicks && <th className="text-center" title="WhatsApp delivery"><WhatsappLogo size={14} weight="fill" className="inline text-[#128C7E]" /></th>}<th className="text-right">Actions</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={colSpan} className="text-center py-10 text-ink-muted">{statusFilter ? <>No {statusFilter} {cfg.label.toLowerCase()}s. <button className="underline font-semibold" onClick={() => setStatusFilter("")} data-testid="clear-status-filter">Show all</button></> : <>No {cfg.label.toLowerCase()} yet. Click <span className="font-semibold">New {cfg.label}</span>.</>}</td></tr>}
              {sorted.map(r => (
                <tr key={r.id} data-testid={`doc-row-${r.id}`}>
                  <td><input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} data-testid={`select-${r.id}`} /></td>
                  <td><Link to={`/${cfg.path}/${r.id}`} className="text-brand font-semibold hover:underline" data-testid={`doc-number-${r.id}`}>{r.doc_number}</Link></td>
                  <td>{fmtDate(r.doc_date)}</td>
                  <td>{r.party_snapshot?.name || "—"}</td>
                  {showItems && <td className="align-top py-3 min-w-[320px]"><OrderItemsCell items={r.items} docId={r.id} /></td>}
                  {showDelivery && <td><DeliveryDateCell date={r.expected_delivery_date} status={r.status} docId={r.id} /></td>}
                  {showTaxable && <td className="text-right tabular">{fmtINR(r.total_taxable)}</td>}
                  <td className="text-right font-semibold tabular">{fmtINR(r.grand_total)}</td>
                  <td><span className={`status-pill ${statusClass(displayStatus(r.status))}`} data-testid={`status-${r.id}`}>{displayStatus(r.status)}</span></td>
                  {showWaTicks && <td className="text-center" data-testid={`wa-tick-${r.id}`}><DeliveryTick status={deliveries[r.id]?.status} sentAt={deliveries[r.id]?.sent_at} phone={deliveries[r.id]?.phone} /></td>}
                  <td className="text-right whitespace-nowrap">
                    <Link to={`/${cfg.path}/${r.id}`} className="btn-ghost inline-flex items-center gap-1" data-testid={`view-doc-${r.id}`}><Eye size={14} /> View</Link>
                    {!cfg.listItems && <Link to={`/${cfg.path}/${r.id}/edit`} className="btn-ghost inline-flex items-center gap-1" data-testid={`edit-doc-${r.id}`}><PencilSimple size={14} /> Edit</Link>}
                    {!cfg.listItems && <button onClick={() => remove(r.id)} className="btn-ghost inline-flex items-center gap-1 text-destructive" data-testid={`delete-doc-${r.id}`}><Trash size={14} /> Delete</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
